import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { useAppDispatch } from '@/app/hooks';
import type { AppError } from '@/features/auth/types';
import type { EvergreenScheduledInteractionType } from '@/features/webinar/types';
import {
  deleteEvergreenScheduledInteraction,
  saveEvergreenScheduledInteraction,
} from '@/features/webinar/webinarThunks';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import Loader from '@/components/ui/Loader';
import {
  hmsToSeconds,
  secondsToHms,
  toScheduleError,
  validateScheduleWindow,
  type Hms,
  type ScheduleError,
  type ScheduledWindow,
} from '@/lib/evergreenInteractionSchedule';
import HmsInputs from './HmsInputs';

type Row = {
  key: string;
  id: number | null;
  start: Hms;
  end: Hms;
};

type ScheduledWindowsEditorProps = {
  workspaceId: number;
  webinarId: number;
  type: EvergreenScheduledInteractionType;
  resourceId: number;
  windows: ScheduledWindow[];
  videoDurationSeconds: number | null;
  readOnly?: boolean;
};

const EMPTY_HMS: Hms = { h: '00', m: '00', s: '00' };

function withoutKey<T>(record: Record<string, T>, key: string): Record<string, T> {
  const next = { ...record };
  delete next[key];
  return next;
}

function rowFromWindow(window: ScheduledWindow): Row {
  return {
    key: `saved-${window.id}`,
    id: window.id,
    start: secondsToHms(window.startSeconds),
    end: secondsToHms(window.endSeconds),
  };
}

export default function ScheduledWindowsEditor({
  workspaceId,
  webinarId,
  type,
  resourceId,
  windows,
  videoDurationSeconds,
  readOnly,
}: ScheduledWindowsEditorProps) {
  const { t } = useTranslation('createWebinar');
  const dispatch = useAppDispatch();
  const [drafts, setDrafts] = useState<Record<string, Row>>({});
  const [newRows, setNewRows] = useState<Row[]>([]);
  const [errors, setErrors] = useState<Record<string, ScheduleError>>({});
  const [pendingKeys, setPendingKeys] = useState<ReadonlySet<string>>(new Set());
  // Le state n'est relu qu'au rendu suivant : deux clics rapprochés passeraient tous les deux.
  const inFlightRef = useRef(new Set<string>());

  const startPending = (key: string): boolean => {
    if (inFlightRef.current.has(key)) return false;
    inFlightRef.current.add(key);
    setPendingKeys(new Set(inFlightRef.current));
    return true;
  };

  const endPending = (key: string) => {
    inFlightRef.current.delete(key);
    setPendingKeys(new Set(inFlightRef.current));
  };

  const rows: Row[] = [
    ...windows.map((window) => drafts[`saved-${window.id}`] ?? rowFromWindow(window)),
    ...newRows,
  ];

  const clearRowState = (key: string) => {
    setDrafts((current) => withoutKey(current, key));
    setErrors((current) => withoutKey(current, key));
  };

  const updateRow = (row: Row, patch: Partial<Pick<Row, 'start' | 'end'>>) => {
    const next = { ...row, ...patch };
    if (row.id == null) {
      setNewRows((current) => current.map((r) => (r.key === row.key ? next : r)));
    } else {
      setDrafts((current) => ({ ...current, [row.key]: next }));
    }
    setErrors((current) => withoutKey(current, row.key));
  };

  const addRow = () => {
    setNewRows((current) => [
      ...current,
      { key: `new-${crypto.randomUUID()}`, id: null, start: EMPTY_HMS, end: EMPTY_HMS },
    ]);
  };

  const saveRow = async (row: Row) => {
    const startSeconds = hmsToSeconds(row.start);
    const endSeconds = hmsToSeconds(row.end);
    const validationError = validateScheduleWindow(startSeconds, endSeconds, videoDurationSeconds);
    if (validationError || startSeconds == null || endSeconds == null) {
      setErrors((current) => ({ ...current, [row.key]: validationError ?? 'INVALID_TIME' }));
      return;
    }
    if (!startPending(row.key)) return;
    try {
      await dispatch(
        saveEvergreenScheduledInteraction({
          workspaceId,
          webinarId,
          interactionId: row.id ?? undefined,
          input: {
            type,
            resourceId,
            startOffsetSeconds: startSeconds,
            endOffsetSeconds: endSeconds,
          },
        }),
      ).unwrap();
      if (row.id == null) {
        setNewRows((current) => current.filter((r) => r.key !== row.key));
      }
      clearRowState(row.key);
    } catch (error) {
      setErrors((current) => ({ ...current, [row.key]: toScheduleError(error as AppError) }));
    } finally {
      endPending(row.key);
    }
  };

  const deleteRow = async (row: Row) => {
    if (row.id == null) {
      setNewRows((current) => current.filter((r) => r.key !== row.key));
      clearRowState(row.key);
      return;
    }
    if (!startPending(row.key)) return;
    try {
      await dispatch(
        deleteEvergreenScheduledInteraction({ workspaceId, webinarId, interactionId: row.id }),
      ).unwrap();
      clearRowState(row.key);
    } catch (error) {
      setErrors((current) => ({ ...current, [row.key]: toScheduleError(error as AppError) }));
    } finally {
      endPending(row.key);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {rows.length === 0 && (
        <div className="text-sm text-secondary py-2">{t('timeCodeForm.noSlot')}</div>
      )}
      {rows.map((row, index) => {
        const busy = pendingKeys.has(row.key);
        const locked = readOnly || busy;
        return (
          <div key={row.key} className="flex flex-col gap-4">
            {index > 0 && <Separator className="my-1" />}
            <div className="flex flex-col md:flex-row items-center justify-between w-full gap-6 rounded-lg">
              <HmsInputs
                label={t('time.startLabel')}
                value={row.start}
                disabled={locked}
                onChange={(start) => updateRow(row, { start })}
              />
              <HmsInputs
                label={t('time.endLabel')}
                value={row.end}
                disabled={locked}
                onChange={(end) => updateRow(row, { end })}
              />
            </div>
            {errors[row.key] && (
              <p className="text-xs text-error">{t(`timeCodeForm.errors.${errors[row.key]}`)}</p>
            )}
            {!readOnly && (
              <div className="flex justify-end gap-4">
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy}
                  onClick={() => void deleteRow(row)}
                  aria-label={t('timeCodeForm.deleteSlot')}
                >
                  <Trash2 className="size-4" />
                </Button>
                <Button type="button" disabled={busy} onClick={() => void saveRow(row)}>
                  {busy && <Loader />}
                  {t('timeCodeForm.saveSlot')}
                </Button>
              </div>
            )}
          </div>
        );
      })}
      {!readOnly && (
        <button type="button" onClick={addRow} className="flex items-center gap-2 pt-2">
          <Plus className="size-6" />
          <Label className="cursor-pointer">{t('timeCodeForm.addSlot')}</Label>
        </button>
      )}
    </div>
  );
}
